import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Handshake, Scale, Users } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { SectionHeading } from '../components/SectionHeading';
import { CTASection } from '../components/CTASection';
import { ServiceCard, StepCard, BlogCard, AdviseurCard } from '../components/Cards';
import { blogs, advisors } from '../data/mockData';
export function Home() {
  return <div className="w-full" data-id="element-513">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-charcoal" data-id="element-514">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-515">
          <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2000" alt="Corporate building" className="w-full h-full object-cover opacity-30" data-id="element-516" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/80 to-transparent" data-id="element-517" />
        </div>

        {/* Decorative Half Circles */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/5 rounded-full z-0 pointer-events-none" data-id="element-518" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/10 rounded-full z-0 pointer-events-none" data-id="element-519" />

        <div className="max-w-[1440px] w-full mx-auto px-6 lg:px-12 relative z-10" data-id="element-520">
          <AnimatedSection className="max-w-3xl" data-id="element-521">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white leading-tight tracking-tight mb-6" data-id="element-522">
              Wat is uw bedrijf <br data-id="element-523" />
              <span className="font-medium" data-id="element-524">écht waard?</span>
            </h1>
            <p className="text-lg md:text-xl text-warmgray/80 mb-10 max-w-2xl font-light leading-relaxed" data-id="element-525">
              Krijg strategisch inzicht met een objectieve, professionele
              bedrijfswaardering. Essentieel bij verkoop, overname of
              geschillen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-526">
              <Link to="/bedrijfswaardering" className="bg-accent hover:bg-accent-hover text-white px-8 py-4 rounded-full text-base font-medium transition-all text-center" data-id="element-527">
                Start waardebepaling
              </Link>
              <Link to="/contact" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full text-base font-medium transition-all text-center" data-id="element-528">
                Plan een gesprek
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Waarom Bedrijfswaardering */}
      <section className="py-24 bg-offwhite" data-id="element-529">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-530">
          <AnimatedSection data-id="element-531">
            <SectionHeading title="Wanneer heeft u een waardering nodig?" subtitle="Situaties" className="mb-16" data-id="element-532" />
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-533">
            <AnimatedSection delay={0.1} data-id="element-534">
              <ServiceCard title="Verkoop" description="Bepaal de juiste vraagprijs en sta sterker in de onderhandelingen bij de verkoop van uw onderneming." link="/bedrijfswaardering-verkoop" icon={<TrendingUp size={24} data-id="element-536" />} data-id="element-535" />
            </AnimatedSection>
            <AnimatedSection delay={0.2} data-id="element-537">
              <ServiceCard title="Overname" description="Voorkom dat u te veel betaalt. Krijg inzicht in de reële waarde en risico's van het targetbedrijf." link="/bedrijfswaardering-overname" icon={<Handshake size={24} data-id="element-539" />} data-id="element-538" />
            </AnimatedSection>
            <AnimatedSection delay={0.3} data-id="element-540">
              <ServiceCard title="Scheiding / Conflict" description="Een onafhankelijke, objectieve waardering voorkomt escalatie bij echtscheiding of aandeelhoudersgeschillen." link="/bedrijfswaardering-scheiding" icon={<Scale size={24} data-id="element-542" />} data-id="element-541" />
            </AnimatedSection>
            <AnimatedSection delay={0.4} data-id="element-543">
              <ServiceCard title="Interne Overdracht" description="Zorg voor een fiscaal geaccepteerde waardering bij overdracht binnen de familie of aan het management." link="/bedrijfswaardering-interne-overdracht" icon={<Users size={24} data-id="element-545" />} data-id="element-544" />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Hoe Wij Werken */}
      <section className="py-24 bg-white" data-id="element-546">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-547">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-548">
            <div className="lg:w-1/3" data-id="element-549">
              <AnimatedSection data-id="element-550">
                <SectionHeading title="Een helder en gestructureerd proces" subtitle="Hoe wij werken" className="mb-6" data-id="element-551" />
                <p className="text-charcoal/70 leading-relaxed mb-8" data-id="element-552">
                  Een bedrijfswaardering is meer dan een rekensom. Wij kijken
                  naar de historie, de markt, de risico's en de toekomstpotentie
                  van uw onderneming. Ons proces is transparant en efficiënt.
                </p>
                <Link to="/werkwijze" className="inline-flex items-center text-accent font-medium hover:text-accent-hover transition-colors group" data-id="element-553">
                  Bekijk onze volledige werkwijze
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-554" />
                </Link>
              </AnimatedSection>
            </div>

            <div className="lg:w-2/3 grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 md:pt-0" data-id="element-555">
              <AnimatedSection delay={0.1} data-id="element-556">
                <StepCard number="1" title="Intake & Analyse" description="We bespreken uw situatie, verzamelen de benodigde financiële data en analyseren uw bedrijfsmodel." data-id="element-557" />
              </AnimatedSection>
              <AnimatedSection delay={0.2} data-id="element-558">
                <StepCard number="2" title="Waardebepaling" description="We passen de juiste waarderingsmethoden toe (zoals DCF) en normaliseren de cijfers waar nodig." data-id="element-559" />
              </AnimatedSection>
              <AnimatedSection delay={0.3} data-id="element-560">
                <StepCard number="3" title="Rapportage" description="U ontvangt een helder, onderbouwd rapport dat u direct kunt gebruiken voor uw specifieke doel." data-id="element-561" />
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Kennisbank Preview */}
      <section className="py-24 bg-offwhite" data-id="element-562">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-563">
          <AnimatedSection className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-564">
            <SectionHeading title="Inzicht & Kennis" subtitle="Kennisbank" data-id="element-565" />
            <Link to="/kennisbank" className="inline-flex items-center text-charcoal font-medium hover:text-accent transition-colors group pb-2" data-id="element-566">
              Naar kennisbank
              <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-567" />
            </Link>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-568">
            {blogs.slice(0, 3).map((blog, index) => <AnimatedSection key={blog.id} delay={index * 0.1} data-id="element-569">
                <BlogCard {...blog} data-id="element-570" />
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* Adviseurs Preview */}
      <section className="py-24 bg-white" data-id="element-571">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-572">
          <AnimatedSection className="text-center mb-16" data-id="element-573">
            <SectionHeading title="Vind de juiste specialist" subtitle="Onze Adviseurs" align="center" className="mx-auto" data-id="element-574" />
            <p className="text-charcoal/70 max-w-2xl mx-auto mt-6" data-id="element-575">
              Werk samen met gecertificeerde Register Valuators en ervaren M&A
              adviseurs die uw sector begrijpen.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-576">
            {advisors.slice(0, 4).map((advisor, index) => <AnimatedSection key={advisor.id} delay={index * 0.1} data-id="element-577">
                <AdviseurCard {...advisor} data-id="element-578" />
              </AnimatedSection>)}
          </div>

          <AnimatedSection className="text-center" data-id="element-579">
            <Link to="/adviseurs" className="inline-flex items-center justify-center px-8 py-3 border border-charcoal/20 rounded-full text-charcoal font-medium hover:bg-charcoal hover:text-white transition-all" data-id="element-580">
              Bekijk alle adviseurs
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-581" />
    </div>;
}